import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Printer } from 'lucide-react';
import { Button, Spinner, buttonStyles } from '@zt/ui';
import { api, ApiError } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { bpsLabel, useMoney } from '../lib/money';
import type { OrderProforma } from '../lib/types';
import { usePairLabel, useBulletinDate } from './fx';
import { addressLines } from './order-tools';

/**
 * Proforma fatura (F7-13, ADR-118): yurt dışı gönderide gümrük ve alıcı için sipariş belgesi (tarayıcıdan yazdır
 * veya PDF olarak kaydet). Ticari fatura değildir. Satırlarda GTİP/HS kodu, menşe ülke ve ağırlık; eksikler panelde
 * uyarılır (yazdırılmaz).
 */
export function OrderProformaPage() {
  const t = useT();
  const money = useMoney();
  const { locale, formatDate, formatNumber } = useI18n();
  const pair = usePairLabel();
  const day = useBulletinDate();
  const { id } = useParams({ from: '/app/siparisler/$id/proforma' });
  const q = useQuery({
    queryKey: ['order-proforma', id],
    queryFn: () => api.get<OrderProforma>(`/v1/admin/orders/${id}/proforma`),
    retry: false,
  });
  const region = (() => {
    try {
      return new Intl.DisplayNames([locale], { type: 'region' });
    } catch {
      return null;
    }
  })();
  const country = (code: string | null | undefined) => (code ? (region?.of(code) ?? code) : '—');
  const kg = (grams: number | null) =>
    grams === null ? '—' : t('admin.proforma.kg', { value: formatNumber(grams / 1000, 3) });
  const d = q.data;
  return (
    <PageFrame
      helpKey="page.order_proforma"
      title={t('admin.proforma.title')}
      printable
      actions={
        <span className="inline-flex items-center gap-1">
          <Link to="/siparisler/$id" params={{ id }} className={buttonStyles({ variant: 'secondary' })}>
            {t('admin.proforma.back')}
          </Link>
          <Button variant="primary" onClick={() => window.print()} disabled={!d}>
            <Printer className="h-4 w-4" aria-hidden />
            {t('admin.orderPrint.print')}
          </Button>
          <HelpTip k="orders.proforma" />
        </span>
      }
    >
      {q.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : !d ? (
        <p role="alert" className="text-sm text-rose">
          {q.error instanceof ApiError ? q.error.title : t('admin.orderPrint.nothing')}
        </p>
      ) : (
        <>
          {d.missingCustoms.length > 0 && (
            <p
              role="alert"
              className="mb-4 rounded-md border border-amber/40 bg-amber/10 px-4 py-3 text-sm print:hidden"
            >
              {t('admin.proforma.missingCustoms', { skus: d.missingCustoms.join(', ') })}
            </p>
          )}
          {d.taxExemption === 'export' && (
            <p className="mb-4 text-sm text-ink-2 print:hidden">{t('admin.proforma.exemptionVerify')}</p>
          )}
          <article
            aria-label={t('admin.proforma.documentLabel', { number: d.number })}
            className="space-y-5 rounded-md border border-line bg-surface p-6 text-sm text-ink print:rounded-none print:border-0 print:p-0"
          >
            <header className="flex flex-wrap items-start justify-between gap-4 border-b border-line pb-4">
              <div className="space-y-0.5">
                <p className="font-semibold">{d.seller.name}</p>
                {d.seller.address && <p className="whitespace-pre-line">{d.seller.address}</p>}
                <p>{country(d.seller.countryCode)}</p>
                {(d.seller.taxOffice || d.seller.taxNumber) && (
                  <p>
                    {t('admin.proforma.sellerTax', {
                      office: d.seller.taxOffice ?? '—',
                      number: d.seller.taxNumber ?? '—',
                    })}
                  </p>
                )}
                {d.seller.mersisNo && <p>{t('admin.proforma.mersis', { no: d.seller.mersisNo })}</p>}
                {[d.seller.phone, d.seller.email].filter(Boolean).length > 0 && (
                  <p>{[d.seller.phone, d.seller.email].filter(Boolean).join(' / ')}</p>
                )}
              </div>
              <div className="text-end">
                <h2 className="font-cond text-2xl font-semibold">{t('admin.proforma.title')}</h2>
                <p className="font-cond text-xl font-semibold tabular">{d.number}</p>
                <p className="text-ink-2">{formatDate(d.date, false)}</p>
                <p className="text-ink-2">{t('admin.proforma.currency', { currency: d.currency })}</p>
              </div>
            </header>
            <p className="text-xs text-ink-2">{t('admin.proforma.notInvoice')}</p>
            <div className="grid gap-4 sm:grid-cols-2 print:grid-cols-2">
              <section>
                <h3 className="mb-1 font-medium">{t('admin.proforma.buyer')}</h3>
                {d.buyer.name && <p>{d.buyer.name}</p>}
                {addressLines(d.buyer.billingAddress ?? d.buyer.shippingAddress).map((l) => (
                  <p key={l}>{l}</p>
                ))}
                <p>
                  {country(
                    ((d.buyer.billingAddress ?? d.buyer.shippingAddress)?.countryCode as
                      string | undefined) ?? null,
                  )}
                </p>
                {d.buyer.email && <p>{d.buyer.email}</p>}
              </section>
              <section>
                <h3 className="mb-1 font-medium">{t('admin.proforma.consignee')}</h3>
                {addressLines(d.buyer.shippingAddress).map((l) => (
                  <p key={l}>{l}</p>
                ))}
                <p>{country((d.buyer.shippingAddress?.countryCode as string | undefined) ?? null)}</p>
                <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                  <dt className="text-ink-2">{t('admin.orders.shippingMethod')}</dt>
                  <dd>{d.shippingMethodName ?? '—'}</dd>
                  <dt className="text-ink-2">{t('admin.proforma.totalWeight')}</dt>
                  <dd className="tabular">{kg(d.totalWeightGrams)}</dd>
                </dl>
              </section>
            </div>
            <div className="overflow-x-auto print:overflow-visible">
              <table className="w-full min-w-[44rem] border-collapse print:min-w-0">
                <thead>
                  <tr className="border-b border-line text-ink-2">
                    <th className="py-1.5 pe-2 text-start font-medium">{t('admin.products.sku')}</th>
                    <th className="py-1.5 pe-2 text-start font-medium">{t('admin.proforma.description')}</th>
                    <th className="py-1.5 pe-2 text-start font-medium">{t('admin.proforma.hsCode')}</th>
                    <th className="py-1.5 pe-2 text-start font-medium">{t('admin.proforma.origin')}</th>
                    <th className="py-1.5 pe-2 text-end font-medium">{t('admin.orders.qty')}</th>
                    <th className="py-1.5 pe-2 text-end font-medium">{t('admin.proforma.weight')}</th>
                    <th className="py-1.5 pe-2 text-end font-medium">{t('admin.orders.unitPrice')}</th>
                    <th className="py-1.5 pe-2 text-end font-medium">{t('admin.orders.tax')}</th>
                    <th className="py-1.5 text-end font-medium">{t('admin.orders.lineTotal')}</th>
                  </tr>
                </thead>
                <tbody>
                  {d.lines.map((l, i) => (
                    <tr key={`${l.sku}-${i}`} className="border-b border-line align-top">
                      <td className="py-1.5 pe-2 tabular">{l.sku}</td>
                      <td className="py-1.5 pe-2">
                        {l.title}
                        {l.customsDescription && (
                          <span className="block text-ink-2">{l.customsDescription}</span>
                        )}
                      </td>
                      <td className="py-1.5 pe-2 tabular">{l.hsCode ?? '—'}</td>
                      <td className="py-1.5 pe-2">{l.originCountry ? country(l.originCountry) : '—'}</td>
                      <td className="py-1.5 pe-2 text-end tabular">{l.quantity}</td>
                      <td className="py-1.5 pe-2 text-end tabular">{kg(l.weightGrams)}</td>
                      <td className="py-1.5 pe-2 text-end tabular">{money(l.unitPriceMinor, d.currency)}</td>
                      <td className="py-1.5 pe-2 text-end tabular">{bpsLabel(l.taxRateBps)}</td>
                      <td className="py-1.5 text-end tabular">{money(l.lineTotalMinor, d.currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <dl className="ms-auto grid max-w-xs grid-cols-[1fr_auto] gap-x-4 gap-y-0.5">
              <dt className="text-ink-2">{t('admin.orders.subtotal')}</dt>
              <dd className="text-end tabular">{money(d.totals.subtotalMinor, d.currency)}</dd>
              {d.totals.discountMinor > 0 && (
                <>
                  <dt className="text-ink-2">{t('admin.proforma.discount')}</dt>
                  <dd className="text-end tabular">−{money(d.totals.discountMinor, d.currency)}</dd>
                </>
              )}
              <dt className="text-ink-2">{t('admin.orders.shipping')}</dt>
              <dd className="text-end tabular">{money(d.totals.shippingMinor, d.currency)}</dd>
              <dt className="text-ink-2">{t('admin.orders.tax')}</dt>
              <dd className="text-end tabular">{money(d.totals.taxMinor, d.currency)}</dd>
              {d.totals.cancelledItemsMinor > 0 && (
                <>
                  <dt className="text-ink-2">{t('admin.orders.cancelledItems')}</dt>
                  <dd className="text-end tabular">−{money(d.totals.cancelledItemsMinor, d.currency)}</dd>
                </>
              )}
              <dt className="font-medium">{t('admin.orders.total')}</dt>
              <dd className="text-end font-semibold tabular">{money(d.totals.totalMinor, d.currency)}</dd>
            </dl>
            <div className="space-y-1 border-t border-line pt-3 text-ink-2">
              {d.taxExemption === 'export' && <p>{t('admin.proforma.exportExemption')}</p>}
              {d.fx?.rate && d.fx.unit && d.fx.bulletinDate && (
                <p>
                  {t('admin.proforma.fxLine', {
                    pair: pair({ currency: d.fx.currency, rate: d.fx.rate, unit: d.fx.unit }),
                    date: day(d.fx.bulletinDate),
                  })}
                </p>
              )}
              {!d.taxExemption && (
                <p>
                  {t(d.taxInclusive ? 'admin.proforma.pricesInclusive' : 'admin.proforma.pricesExclusive')}
                </p>
              )}
            </div>
          </article>
        </>
      )}
    </PageFrame>
  );
}
